/* eslint-disable max-len */
import _ from 'underscore';
import PropTypes from 'prop-types';
import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import { Dropdown, Callout, DirectionalHint } from '@fluentui/react';
import { CheckBoxNode, TreeViewProps } from '@bingads-webui-react/tree-view';
import { TreeViewWithSearchBox } from './tree-view-with-search-box';
import { getSelectedNodes } from './utils';

export const DropdownTreeViewSelector = ({
  searchBoxPlaceholder,
  treeViewProps,
  dropdownProps,
  onChange,
}) => {
  const dropdownRootRef = useRef(null);
  // use dummyOptions and selectedKey to make sure customized onRenderTitle always get called
  const dummyOptions = useMemo(() => [{ key: 1 }], []);
  const selectedNodes = useMemo(() => getSelectedNodes(treeViewProps.nodes), [treeViewProps.nodes]);
  const mergedSelectedNodes = useMemo(() => selectedNodes.filter((node) => {
    const parentIds = _.result(node, 'parents', []);
    return !parentIds.some(parentId => selectedNodes.some(parentNode => parentNode.id === parentId));
  }), [selectedNodes]);

  useEffect(() => {
    if (onChange) {
      onChange({
        selectedNodes,
        mergedSelectedNodes,
      });
    }
  }, [selectedNodes, mergedSelectedNodes, onChange]);

  const onRenderContainer = useCallback((props) => {
    const { calloutProps, dropdownWidth, onDismiss } = props;

    let calloutWidth;
    let calloutMinWidth;
    calloutMinWidth = dropdownRootRef.current ? dropdownRootRef.current.clientWidth : 0;
    if (dropdownWidth === 'auto') {
      calloutMinWidth = dropdownRootRef.current ? dropdownRootRef.current.clientWidth : 0;
    } else {
      calloutWidth = dropdownWidth || (dropdownRootRef.current ? dropdownRootRef.current.clientWidth : 0);
    }

    return (
      <Callout
        isBeakVisible={false}
        gapSpace={0}
        doNotLayer={false}
        directionalHintFixed={false}
        directionalHint={DirectionalHint.bottomLeftEdge}
        calloutWidth={calloutWidth}
        calloutMinWidth={calloutMinWidth}
        {...calloutProps}
        target={dropdownRootRef.current}
        onDismiss={onDismiss}
      >
        <TreeViewWithSearchBox
          searchBoxPlaceholder={searchBoxPlaceholder}
          treeViewProps={{
          nodeRenderer: CheckBoxNode,
          autoHeight: true,
          ...treeViewProps,
        }}
        />
      </Callout>
    );
  }, [searchBoxPlaceholder, treeViewProps]);

  const onRenderTitle = useCallback(() => (
    <React.Fragment>
      {mergedSelectedNodes.map(item => item.name).join(', ')}
    </React.Fragment>
  ), [mergedSelectedNodes]);

  return (
    <Dropdown
      options={dummyOptions}
      selectedKey={1}
      onRenderContainer={onRenderContainer}
      onRenderTitle={onRenderTitle}
      ref={dropdownRootRef}
      {...dropdownProps}
    />
  );
};

DropdownTreeViewSelector.propTypes = {
  searchBoxPlaceholder: PropTypes.string,
  treeViewProps: PropTypes.shape(TreeViewProps),
  dropdownProps: PropTypes.shape({}),
  onChange: PropTypes.func,
};

DropdownTreeViewSelector.defaultProps = {
  searchBoxPlaceholder: '',
  treeViewProps: {},
  dropdownProps: {},
  onChange: null,
};
